import axios from "axios";
import Button from "../../Button";
import constants from "../../../constants";
import { useLocation } from "react-router-dom";
import React, { useContext, useEffect, useState, useRef } from "react";
import ProctorEyeSvg from "../../../Svg/ProctorEyeSvg";
import AlertContext from "../../../context/AlertContext";
import WebSocketHooks from "../../../Hooks/WebSocketHooks";
import useCommonFunctionHooks from "../../../Hooks/useCommonFunctionHooks";
import MobileViewSetUpHooks from "../../../Hooks/InstructionPage/MobileViewSetUpHooks";
import { getDataFromStorage } from "../../../util";

export default function MobileViewInstruction() {
  const Alert = useContext(AlertContext);
  const WebSocketHks = WebSocketHooks();
  const MobileViewSetUpHks = MobileViewSetUpHooks();
  const { getCatchBlockDetails } = useCommonFunctionHooks();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [wsReady, setWsReady] = useState(false);
  
  // Track if WebSocket initialization was attempted
  const wsInitAttempted = useRef(false);

  // Initialize WebSocket connection when component mounts
  useEffect(() => {
    const initializeWebSocket = async () => {
      if (wsInitAttempted.current) return;
      wsInitAttempted.current = true;

      try {
        console.log("Initializing WebSocket...");
        await WebSocketHks.initializeWebSocket();
        setWsReady(true);
        console.log("WebSocket initialized successfully");
      } catch (error) {
        console.warn("WebSocket initialization failed:", error);
        setWsReady(false);
        // Don't block the app if WebSocket fails
      }
    };

    initializeWebSocket();
  }, []);

  // Verify token
  useEffect(() => {
    const verifyToken = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      if (!token) {
        setError("No token provided. Please scan the QR code again.");
        setLoading(false);
        Alert.setShowNotify({
          show: true,
          title: "Error",
          msg: "Invalid connection. Please scan the QR code again.",
        });
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const res = await axios.post(
          `${MobileViewSetUpHks.pathName()}node/learner/dp/QR/verify`,
          JSON.stringify({ token }),
          {
            timeout: 60000,
            headers: {
              Authorization: "abdjcdifouvn",
              "Content-type": "application/json",
            },
          }
        );

        if (res.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
          Alert.setResultCode(constants.RESULT_STATUS.SUCCESS);
          localStorage.setItem("tokenKey", res.data.data.token);
          setTokenValid(true);
        } else {
          setError(res.data.msg || "Token validation failed");
          Alert.setShowNotify({
            show: true,
            title: "Warning",
            msg: res.data.msg || "Token validation failed. Please try again.",
          });
        }
      } catch (error) {
        console.error("Token verification error:", error);
        setError(error.message || "Connection failed");
        
        if (error.code === "ECONNABORTED") {
          Alert.setShowNotify({
            show: true,
            title: "Connection Timeout",
            msg: "The connection timed out. Please check your internet connection and try again.",
          });
        } else if (error.response?.status === 401) {
          Alert.setShowNotify({
            show: true,
            title: "Token Expired",
            msg: "This QR code has expired. Please generate a new one on your laptop.",
          });
        } else {
          Alert.setShowNotify({
            show: true,
            title: "Connection Error",
            msg: "Failed to connect. Please check your internet connection and try again.",
          });
        }
        
        getCatchBlockDetails(error);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [location.search]);

  // Improved WebSocket connection helper
  const ensureWebSocketConnection = async () => {
    try {
      // If WebSocket hook has a connection method, use it
      if (WebSocketHks.ensureConnection) {
        return await WebSocketHks.ensureConnection();
      }

      // Fallback to direct WebSocket access
      const ws = WebSocketHks.wsRef?.current;
      
      if (!ws) {
        console.warn("WebSocket not initialized");
        return null;
      }

      if (ws.readyState === WebSocket.OPEN) {
        return ws;
      }

      if (ws.readyState === WebSocket.CONNECTING) {
        return await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            ws.removeEventListener('open', onOpen);
            ws.removeEventListener('error', onError);
            reject(new Error('WebSocket connection timeout'));
          }, 10000);

          const onOpen = () => {
            clearTimeout(timeout);
            ws.removeEventListener('error', onError);
            resolve(ws);
          };

          const onError = (error) => {
            clearTimeout(timeout);
            ws.removeEventListener('open', onOpen);
            reject(new Error(`WebSocket connection failed: ${error}`));
          };

          ws.addEventListener('open', onOpen, { once: true });
          ws.addEventListener('error', onError, { once: true });
        });
      }

      console.warn(`WebSocket in state: ${ws.readyState}`);
      return null;
    } catch (error) {
      console.warn("WebSocket connection failed:", error);
      return null;
    }
  };

  const handleProceed = async () => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    
    try {
      setLoading(true);
      
      // Try to establish WebSocket connection (but don't block if it fails)
      let ws = null;
      try {
        ws = await ensureWebSocketConnection();
        if (ws) {
          console.log("WebSocket connected successfully");
        }
      } catch (wsError) {
        console.warn("WebSocket connection attempt failed, proceeding without it:", wsError);
      }
      
      const res = await axios.post(
        `${MobileViewSetUpHks.pathName()}node/learner/dp/status/update`,
        {
          userId: getDataFromStorage("userId"),
          status: 1,
          token,
        },
        {
          timeout: 60000,
          headers: {
            Authorization: "abdjcdifouvn",
            "Content-type": "application/json",
          },
        }
      );

      console.log("Status update response: ", res.data);
      
      if (res.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
        // Store required data in localStorage before navigation
        localStorage.setItem("status", "1");
        
        const userId = getDataFromStorage("userId");
        if (userId) {
          localStorage.setItem("userId", userId);
        }
        
        // Store notification that image will be captured
        Alert.setShowNotify({
          show: true,
          title: "Success",
          msg: "Proceeding to camera setup. Image will be captured automatically.",
        });

        await handleSuccessfulStatusUpdate(ws);
      } else {
        Alert.setShowNotify({
          show: true,
          title: "Warning",
          msg: res.data.msg || "Failed to update status. Please try again.",
        });
      }
    } catch (error) {
      console.error("Proceed error:", error);
      Alert.setShowNotify({
        show: true,
        title: "Error",
        msg: "Failed to proceed. Please check your connection and try again.",
      });
      getCatchBlockDetails(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful status update with WebSocket
  const handleSuccessfulStatusUpdate = async (ws) => {
    try {
      // If we have a working WebSocket connection, send verification
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          // Make sure we're sending the correct data format
          const message = typeof constants.WEBSOCKET.VERIFY_TOKEN === 'string' 
            ? constants.WEBSOCKET.VERIFY_TOKEN 
            : JSON.stringify(constants.WEBSOCKET.VERIFY_TOKEN);
          
          ws.send(message);
          console.log("WebSocket verification token sent:", message);
        } catch (sendError) {
          console.warn("Failed to send WebSocket message:", sendError);
        }
      } else {
        console.warn("WebSocket not available for verification");
      }
      
      // Navigate to setup regardless of WebSocket status
      Alert.navigate("dp/setup");
      
    } catch (error) {
      console.warn("WebSocket operation failed, but proceeding with navigation:", error);
      Alert.navigate("dp/setup");
    }
  };

  return (
    <div className="dpBody">
      <div className="dpBodyUpper">
        <div className="dpBodyHeader">
          <ProctorEyeSvg type={2} />
          <div className="dpBodyHeaderLabel">Proctored Test</div>
        </div>
        
        {/* WebSocket status indicator for debugging */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', margin: '5px 0' }}>
            WebSocket: {wsReady ? 'Ready' : 'Not Ready'}
          </div>
        )}
        
        {loading && !error && (
          <div className="dpBodyMain" style={{ textAlign: 'center', padding: '20px' }}>
            <div>Verifying connection...</div>
            <div style={{ marginTop: '10px', color: '#667085' }}>Please wait</div>
          </div>
        )}
        
        {error && !loading && (
          <div className="dpBodyMain" style={{ textAlign: 'center', padding: '20px', color: '#F04438' }}>
            <div>{error}</div>
            <div style={{ marginTop: '10px', fontSize: '14px' }}>
              Please scan the QR code again from your laptop.
            </div>
          </div>
        )}
        
        {tokenValid && !loading && (
          <div className="dpBodyMain">
            Instructions to Proceed:
            <ul className="dpBodyMainUl">
              <li>
                This phone will monitor your surroundings throughout the test.
              </li>
              <li>
                Ensure your phone is fully charged or plugged in. Stay connected
                to a stable Wi-Fi or data network.
              </li>
              <li>Clear your surroundings of any unauthorized materials.</li>
              <li>
                Grant camera access when prompted, this is required for
                proctoring.
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="dpBodyFooter">
        <Button
          size="md"
          onClick={handleProceed}
          disable={loading || error || Alert.resultCode !== constants.RESULT_STATUS.SUCCESS}
          hierarchy={{
            type: "primary",
            buttonText: loading ? "Please wait..." : "Proceed",
          }}
        />
      </div>
    </div>
  );
}